import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Collapse,
  Dialog,
  Divider,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import {
  CheckCircle,
  CheckCircleOutline,
  Close,
  ErrorOutline,
  ExpandMore,
  HourglassBottomOutlined,
  InfoOutlined,
  Refresh,
  UploadFileOutlined,
} from "@mui/icons-material";
import { useIsMobile } from "@/hooks/useIsMobile";
import DropZone from "../components/DropZone";
import FileUploadList, {
  isFailedStatus,
  isSuccessStatus,
  processingLabel,
} from "../components/FileUploadList";
import { V2_COLORS } from "../constants/colors";
import {
  FILE_UPLOAD_STATUS,
  ACCEPT_ATTR,
  DEFAULT_MAX_FILES,
  DEFAULT_MAX_FILE_SIZE_MB,
  type FlowPhase,
  type FileUploadItem,
  type UploadQueueSummary,
  type FileValidationError,
  type BatchEntry,
  type BatchChildStatus,
} from "../types/attachment";

// --- Phase-driven title ---

function getTitle(phase: FlowPhase, total: number): string {
  switch (phase) {
    case "uploading":
      return `Uploading ${total} file(s)...`;
    case "complete":
      return `Upload Complete \u2014 ${total} file(s)`;
    default:
      return "Upload Invoices";
  }
}

// --- Component ---

interface UploadInvoicesModalProps {
  open: boolean;
  onClose: () => void;

  // State from upload flow hook
  phase: FlowPhase;
  items: FileUploadItem[];
  summary: UploadQueueSummary;
  rejectedFiles: FileValidationError[];
  /** Set when startBatchProcessing API call itself failed (not an OCR failure). */
  batchStartError: string | null;
  /** Maximum files allowed for this upload context. 1 for invoice-level re-upload, DEFAULT_MAX_FILES otherwise. */
  maxFiles?: number;

  // State from batch manager
  activeBatches: BatchEntry[];
  onDismissBatch: (batchId: string) => void;

  // Actions
  onFilesSelected: (files: File[]) => void;
  onUploadAll: () => void;
  onRemoveFile: (index: number) => void;
  onRetryFile: (index: number) => void;
  onRetryAllFailed: () => void;
  onRetryBatchProcessing: () => void;
  onDismissRejected: () => void;
}

export default function UploadInvoicesModal({
  open,
  onClose,
  phase,
  items,
  summary,
  rejectedFiles,
  batchStartError,
  maxFiles = DEFAULT_MAX_FILES,
  activeBatches,
  onDismissBatch,
  onFilesSelected,
  onUploadAll,
  onRemoveFile,
  onRetryFile,
  onRetryAllFailed,
  onRetryBatchProcessing,
  onDismissRejected,
}: UploadInvoicesModalProps) {
  const unresolvedCount = items.filter((i) => i.status !== FILE_UPLOAD_STATUS.UPLOADED).length;
  const isMobile = useIsMobile();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth={false}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : "4px", maxWidth: isMobile ? "none" : 640, width: "100%", ...(isMobile ? { display: "flex", flexDirection: "column" } : {}) } }}
    >
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={isMobile ? 1.5 : 2} sx={{ flexShrink: 0 }}>
        <Typography fontWeight={700} fontSize="16px" lineHeight="24px">
          {getTitle(phase, summary.total)}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close sx={{ fontSize: 24, color: V2_COLORS.textSecondary }} />
        </IconButton>
      </Box>

      <Divider />

      {/* Content */}
      <Box px={isMobile ? 2 : 3} py={2} sx={isMobile ? { flex: 1, minHeight: 0, overflowY: "auto" } : { maxHeight: "65vh", overflowY: "auto" }}>
        {/* Info banner */}
        {(phase === "idle" || phase === "selected") && (
          <InfoBanner
            icon={<InfoOutlined sx={{ fontSize: 18, color: V2_COLORS.info }} />}
            bgColor={V2_COLORS.infoBg}
            text={
              maxFiles === 1
                ? `Upload 1 PDF, PNG, or JPG (max ${DEFAULT_MAX_FILE_SIZE_MB}MB) to replace the existing invoice attachment.`
                : `Supports multiple PDF, PNG, JPG files. Max ${DEFAULT_MAX_FILE_SIZE_MB}MB per file. Maximum ${maxFiles} invoices per upload. Duplicate check: Vendor ID + invoice number.`
            }
          />
        )}
        {phase === "uploading" && (
          <InfoBanner
            icon={<HourglassBottomOutlined sx={{ fontSize: 18, color: V2_COLORS.warning }} />}
            bgColor={V2_COLORS.warningBg}
            text="Uploading files to storage. Please wait..."
          />
        )}

        {/* Rejected files warning */}
        {rejectedFiles.length > 0 && phase !== "uploading" && (
          <Alert severity="warning" onClose={onDismissRejected} sx={{ mb: 2 }}>
            {rejectedFiles.length} file(s) rejected:{" "}
            {rejectedFiles.map((r) => `${r.file.name} (${r.reason})`).join(", ")}
          </Alert>
        )}

        {/* Drop zone */}
        {(phase === "idle" || phase === "selected") && (
          <Box mb={phase === "selected" ? 2 : 0}>
            <DropZone
              onFilesSelected={onFilesSelected}
              accept={ACCEPT_ATTR}
              title="Drop invoice files here or click to browse"
              subtitle={`PDF, PNG, JPG \u00B7 Max ${DEFAULT_MAX_FILE_SIZE_MB}MB per file \u00B7 ${maxFiles === 1 ? "1 file per upload" : `Up to ${maxFiles} files per upload`}`}
              ariaLabel="Select invoice files"
              compact={phase === "selected"}
              disabled={items.length >= maxFiles}
              multiple={maxFiles > 1}
            />
            {items.length >= maxFiles && (
              <Typography fontSize="12px" color={V2_COLORS.error} mt={0.5} textAlign="center">
                Maximum {maxFiles} file{maxFiles === 1 ? "" : "s"} reached
              </Typography>
            )}
          </Box>
        )}

        {/* File list */}
        {phase === "selected" && (
          <FileUploadList items={items} phase="selected" onRemoveFile={onRemoveFile} />
        )}
        {phase === "uploading" && <FileUploadList items={items} phase="uploading" />}

        {/* Complete: only upload results — OCR results live in Active Batches below */}
        {phase === "complete" && (
          <>
            {batchStartError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                OCR processing could not be started: {batchStartError}
              </Alert>
            )}
            <SummaryCards uploaded={summary.uploaded} uploadFailed={unresolvedCount} />
            <FileUploadList
              items={items}
              phase="complete"
              batchError={!!batchStartError}
              onRetryFile={onRetryFile}
            />
          </>
        )}

        {/* Active Batches section — shown whenever batchManager has entries */}
        <ActiveBatchesSection batches={activeBatches} onDismiss={onDismissBatch} />
      </Box>

      {/* Footer */}
      <Divider />
      <Box display="flex" gap={1.5} justifyContent="flex-end" px={isMobile ? 2 : 3} py={isMobile ? 1.5 : 2} sx={{ flexShrink: 0 }}>
        <FooterButtons
          phase={phase}
          unresolvedCount={unresolvedCount}
          batchStartError={!!batchStartError}
          onClose={onClose}
          onUploadAll={onUploadAll}
          onRetryAllFailed={onRetryAllFailed}
          onRetryBatchProcessing={onRetryBatchProcessing}
        />
      </Box>
    </Dialog>
  );
}

// --- Active Batches Section ---

function ActiveBatchesSection({
  batches,
  onDismiss,
}: {
  batches: BatchEntry[];
  onDismiss: (batchId: string) => void;
}) {
  const [expandedBatchId, setExpandedBatchId] = useState<string | null>(null);

  if (batches.length === 0) return null;

  const handleToggle = (batchId: string) => {
    setExpandedBatchId((prev) => (prev === batchId ? null : batchId));
  };

  return (
    <Box mt={2}>
      <Divider sx={{ mb: 2 }} />
      <Typography
        fontSize="11px"
        fontWeight={700}
        letterSpacing="0.5px"
        color={V2_COLORS.textSecondary}
        mb={1}
      >
        ACTIVE BATCHES
      </Typography>
      {batches.map((batch) => (
        <BatchRow
          key={batch.batchId}
          batch={batch}
          expanded={expandedBatchId === batch.batchId}
          onToggle={handleToggle}
          onDismiss={onDismiss}
        />
      ))}
    </Box>
  );
}

function BatchRow({
  batch,
  expanded,
  onToggle,
  onDismiss,
}: {
  batch: BatchEntry;
  expanded: boolean;
  onToggle: (batchId: string) => void;
  onDismiss: (batchId: string) => void;
}) {
  const rowRef = useRef<HTMLDivElement>(null);
  const { streamData, files, done } = batch;

  // Scroll the row into view when it opens, after the Collapse animation starts
  useEffect(() => {
    if (!expanded) return;
    const id = setTimeout(() => {
      rowRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 50);
    return () => clearTimeout(id);
  }, [expanded]);

  const total = files.length > 0 ? files.length : (streamData?.total ?? 0);
  const succeeded = streamData?.succeeded ?? 0;
  const failed = streamData?.failed ?? 0;
  const pending = Math.max(0, total - succeeded - failed);

  // Expandable as soon as we have files (from snapshot), stream children, or a known total
  // (total is pre-populated from getActiveBatches() on restore, before SSE children arrive)
  const canExpand = files.length > 0 || (streamData?.children?.length ?? 0) > 0 || (streamData?.total ?? 0) > 0;

  const progress = total > 0 ? Math.round(((succeeded + failed) / total) * 100) : 0;

  // Build a fast lookup: attachmentId → child status from stream
  const childStatusMap = new Map<string, BatchChildStatus>();
  streamData?.children?.forEach((c) => childStatusMap.set(c.attachment_id, c));

  // Primary display list: use file snapshot so ALL files show from the start.
  // For batches restored from BE (files=[]), fall back to streamData.children.
  const rowFiles: { name: string; attachmentId: string }[] =
    files.length > 0
      ? files
      : (streamData?.children?.map((c) => ({ name: c.attachment_id, attachmentId: c.attachment_id })) ?? []);

  return (
    <Box
      ref={rowRef}
      border={`1px solid ${done && failed === 0 ? V2_COLORS.successBg : V2_COLORS.border}`}
      borderRadius="4px"
      mb={1}
      sx={{ backgroundColor: done && failed === 0 ? V2_COLORS.successSoftBg : "transparent" }}
    >
      {/* Row header */}
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        px={1.5}
        py={1}
        sx={{ cursor: canExpand ? "pointer" : "default" }}
        onClick={() => canExpand && onToggle(batch.batchId)}
      >
        {/* Status icon */}
        <Box sx={{ width: 16, height: 16, display: "flex", alignItems: "center", flexShrink: 0 }}>
          {!done ? (
            <CircularProgress size={14} sx={{ color: V2_COLORS.primary }} />
          ) : failed > 0 ? (
            <ErrorOutline sx={{ fontSize: 16, color: V2_COLORS.error }} />
          ) : (
            <CheckCircle sx={{ fontSize: 16, color: V2_COLORS.success }} />
          )}
        </Box>

        {/* Summary text */}
        <Typography fontSize="13px" fontWeight={500} flex={1} noWrap>
          {total} file{total !== 1 ? "s" : ""} &middot;{" "}
          {!done ? "Processing..." : failed > 0 ? "Done with errors" : "Done"}
        </Typography>

        {/* Counts */}
        {succeeded > 0 && (
          <Typography fontSize="12px" color={V2_COLORS.success} sx={{ flexShrink: 0 }}>
            ✓{succeeded}
          </Typography>
        )}
        {failed > 0 && (
          <Typography fontSize="12px" color={V2_COLORS.error} sx={{ flexShrink: 0 }}>
            ✗{failed}
          </Typography>
        )}
        {pending > 0 && (
          <Typography fontSize="12px" color={V2_COLORS.textSecondary} sx={{ flexShrink: 0 }}>
            ⟳{pending}
          </Typography>
        )}

        {/* Expand toggle */}
        {canExpand && (
          <ExpandMore
            sx={{
              fontSize: 18,
              color: V2_COLORS.textSecondary,
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
              flexShrink: 0,
            }}
          />
        )}

        {/* Dismiss — only for completed batches */}
        {done && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDismiss(batch.batchId);
            }}
          >
            <Close sx={{ fontSize: 14, color: V2_COLORS.textSecondary }} />
          </IconButton>
        )}
      </Box>

      {/* In-progress bar */}
      {!done && total > 0 && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ mx: 1.5, mb: 1, height: 3, borderRadius: 2 }}
        />
      )}

      {/* Expanded per-file list — driven by file snapshot so all files show immediately */}
      <Collapse in={expanded && canExpand} unmountOnExit>
        <Box borderTop={`1px solid ${V2_COLORS.border}`} sx={{ maxHeight: 280, overflowY: "auto" }}>
          {rowFiles.map((file, i) => (
            <Box key={file.attachmentId}>
              {i > 0 && <Divider />}
              <BatchChildRow
                fileName={file.name}
                childStatus={childStatusMap.get(file.attachmentId)}
              />
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

function BatchChildRow({
  fileName,
  childStatus,
}: {
  fileName: string;
  childStatus?: BatchChildStatus;
}) {
  const isFailed = childStatus ? isFailedStatus(childStatus.status) : false;
  const isSuccess = childStatus ? isSuccessStatus(childStatus.status) : false;
  const label = childStatus
    ? processingLabel(childStatus.status)
    : { text: "Processing...", color: V2_COLORS.textSecondary };

  const reason = childStatus?.reason;

  return (
    <Box display="flex" alignItems={reason ? "flex-start" : "center"} gap={1.5} px={1.5} py={0.75}>
      <Box
        sx={{ width: 14, height: 14, display: "flex", alignItems: "center", flexShrink: 0, ...(reason ? { mt: "2px" } : {}) }}
      >
        {isFailed ? (
          <ErrorOutline sx={{ fontSize: 14, color: V2_COLORS.error }} />
        ) : isSuccess ? (
          <CheckCircleOutline sx={{ fontSize: 14, color: V2_COLORS.success }} />
        ) : (
          <CircularProgress size={12} sx={{ color: V2_COLORS.primary }} />
        )}
      </Box>
      <Box flex={1} minWidth={0}>
        <Typography fontSize="12px" noWrap color={V2_COLORS.textSecondary}>
          {fileName}
        </Typography>
        {reason && (
          <Typography fontSize="11px" color={V2_COLORS.error} noWrap>
            {reason}
          </Typography>
        )}
      </Box>
      <Typography fontSize="11px" color={label.color} sx={{ flexShrink: 0 }}>
        {label.text}
      </Typography>
    </Box>
  );
}

// --- Sub-components ---

function InfoBanner({
  icon,
  bgColor,
  text,
}: {
  icon: React.ReactNode;
  bgColor: string;
  text: string;
}) {
  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      px={1.5}
      py={1}
      mb={2}
      sx={{ backgroundColor: bgColor, borderRadius: "4px" }}
    >
      {icon}
      <Typography fontSize="13px">{text}</Typography>
    </Box>
  );
}

function SummaryCards({
  uploaded,
  uploadFailed,
}: {
  uploaded: number;
  uploadFailed: number;
}) {
  return (
    <Box display="flex" gap={2} mb={2}>
      <Box
        flex={1}
        display="flex"
        alignItems="center"
        gap={1.5}
        px={2}
        py={1.5}
        sx={{ backgroundColor: V2_COLORS.successSoftBg, borderRadius: "4px" }}
      >
        <CheckCircle sx={{ fontSize: 24, color: V2_COLORS.success }} />
        <Box>
          <Typography fontWeight={700} fontSize="18px" color={V2_COLORS.success}>
            {uploaded}
          </Typography>
          <Typography fontSize="12px" color={V2_COLORS.successSoft}>
            Successfully uploaded
          </Typography>
        </Box>
      </Box>

      {uploadFailed > 0 && (
        <Box
          flex={1}
          display="flex"
          alignItems="center"
          gap={1.5}
          px={2}
          py={1.5}
          sx={{ backgroundColor: V2_COLORS.errorBg, borderRadius: "4px" }}
        >
          <ErrorOutline sx={{ fontSize: 24, color: V2_COLORS.error }} />
          <Box>
            <Typography fontWeight={700} fontSize="18px" color={V2_COLORS.error}>
              {uploadFailed}
            </Typography>
            <Typography fontSize="12px" color={V2_COLORS.error}>
              Failed to upload
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}

// --- Button style constants ---

const btnBase = {
  textTransform: "none" as const,
  fontWeight: 600,
  fontSize: "14px",
  height: 36,
  px: 2,
  boxShadow: "none",
};

const cancelBtn = {
  ...btnBase,
  color: "#666",
  backgroundColor: "#E6E6E6",
  "&:hover": { backgroundColor: "#D6D6D6" },
};

function FooterButtons({
  phase,
  unresolvedCount,
  batchStartError,
  onClose,
  onUploadAll,
  onRetryAllFailed,
  onRetryBatchProcessing,
}: {
  phase: FlowPhase;
  unresolvedCount: number;
  batchStartError: boolean;
  onClose: () => void;
  onUploadAll: () => void;
  onRetryAllFailed: () => void;
  onRetryBatchProcessing: () => void;
}) {
  switch (phase) {
    case "idle":
      return (
        <Button onClick={onClose} sx={cancelBtn}>
          Cancel
        </Button>
      );

    case "selected":
      return (
        <>
          <Button onClick={onClose} sx={cancelBtn}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadFileOutlined sx={{ fontSize: 16 }} />}
            onClick={onUploadAll}
            sx={{
              ...btnBase,
              backgroundColor: V2_COLORS.primary,
              "&:hover": { backgroundColor: V2_COLORS.primaryHover },
            }}
          >
            Upload All
          </Button>
        </>
      );

    case "uploading":
      return (
        <Button disabled sx={{ ...btnBase, backgroundColor: "#E6E6E6" }}>
          Please wait...
        </Button>
      );

    case "complete":
      return (
        <>
          {batchStartError && (
            <Button
              variant="outlined"
              startIcon={<Refresh sx={{ fontSize: 16 }} />}
              onClick={onRetryBatchProcessing}
              sx={{
                ...btnBase,
                color: V2_COLORS.warning,
                borderColor: V2_COLORS.warning,
                "&:hover": { borderColor: V2_COLORS.warning, backgroundColor: V2_COLORS.warningBg },
              }}
            >
              Retry Processing
            </Button>
          )}
          {unresolvedCount > 0 && (
            <Button
              variant="outlined"
              startIcon={<Refresh sx={{ fontSize: 16 }} />}
              onClick={onRetryAllFailed}
              sx={{
                ...btnBase,
                color: V2_COLORS.error,
                borderColor: V2_COLORS.error,
                "&:hover": { borderColor: V2_COLORS.errorHover, backgroundColor: V2_COLORS.errorBg },
              }}
            >
              Retry All Failed ({unresolvedCount})
            </Button>
          )}
          <Button onClick={onClose} sx={cancelBtn}>
            Close
          </Button>
        </>
      );

    default:
      return null;
  }
}
